import { ReactElement, ReactNode } from 'react';
import cn from 'classnames';
import IconWrapper from '@components/ui/Icons';
import { IconType } from '@components/ui/Icons/iconTypes';

import styles from './AlertInlineMessage.module.scss';

export enum AlertStates {
  'INFORMATION',
  'WARNING',
  'ERROR',
  'SUCCESS',
}

export interface AlertInlineMessageProps {
  children: ReactNode;
  messageType?: keyof typeof AlertStates;
  customIcon?: IconType;
  customIconHeight?: number;
  customIconWidth?: number;
  classNames?: string | string[];
  customMessageClassName?: string;
}

const AlertInlineMessage = ({
  children,
  messageType,
  customIcon,
  customIconHeight = 16,
  customIconWidth = 16,
  classNames,
  customMessageClassName,
}: AlertInlineMessageProps): ReactElement | null => {
  if (!children) {
    return null;
  }

  let messageTypeCss;
  let iconType: IconType | null = null;

  switch (messageType) {
    case 'WARNING':
      messageTypeCss = styles.warning;
      iconType = 'warning';
      break;
    case 'ERROR':
      messageTypeCss = styles.error;
      iconType = 'error';
      break;
    case 'SUCCESS':
      messageTypeCss = styles.success;
      iconType = 'success';
      break;
    case 'INFORMATION':
      messageTypeCss = styles.info;
      iconType = 'info';
      break;
    default:
      messageTypeCss = styles.default;
  }

  if (customIcon) {
    iconType = customIcon;
  }
  const messageClasses = cn(styles.alert, messageTypeCss, classNames);
  return (
    <div role="alert" className={messageClasses}>
      <div
        className={cn(styles.messageWrapper, customMessageClassName)}
        aria-live="polite"
      >
        {iconType && (
          <IconWrapper
            height={customIconHeight}
            width={customIconWidth}
            icon={iconType}
            classNames={styles.icon}
          />
        )}
        <div className="message">{children}</div>
      </div>
    </div>
  );
};

export default AlertInlineMessage;
